import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Copy, Send, Lock } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Contextual AI assistant panel for each module.
 * Props:
 *   module: "concept" | "market" | "hr" | "calendar" | "marketing" | "budget"
 *   context: object with current project data for the module
 *   suggestions: array of preset prompt strings
 *   onApply: optional (text) => void  -> if provided, shows an "Apply" button
 */
export default function AIAssistant({ module, context, suggestions = [], onApply }) {
  const { lang, t } = useLang();
  const { canUseAi } = useAuth();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");

  async function ask(promptText) {
    const finalPrompt = (promptText ?? prompt).trim();
    if (!finalPrompt) return;
    setLoading(true);
    setResponse("");
    try {
      const res = await api.post("/ai/assist", {
        module,
        prompt: finalPrompt,
        context: context || {},
        language: lang,
      });
      setResponse(res.data.response || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  function copy() {
    if (!response) return;
    navigator.clipboard.writeText(response);
    toast.success(t("common.copy") + " ✓");
  }

  return (
    <>
      {canUseAi ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 bg-[#F5EFE6] text-[#B89154] border border-[#B89154]/30 hover:bg-[#B89154] hover:text-white transition-all rounded-sm px-4 py-2 text-sm"
          data-testid={`ai-assist-open-${module}-btn`}
        >
          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
          <span className="tracking-wide">{t("common.ai_assist")}</span>
        </button>
      ) : (
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-[#F3F0EA] text-[#8A8886] border border-[#E8E6E1] hover:border-[#B89154] hover:text-[#B89154] transition-all rounded-sm px-4 py-2 text-sm"
          data-testid={`ai-assist-locked-${module}-btn`}
          title={t("paywall.ai_locked_desc")}
        >
          <Lock className="h-4 w-4" strokeWidth={1.5} />
          <span className="tracking-wide">{lang === "es" ? "IA — Activa Starter o Pro" : "AI — Activate Starter or Pro"}</span>
        </Link>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-[#1A1A1A]/30 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`ai-assist-overlay-${module}`}
        >
          <div
            className="w-full max-w-md bg-white border-l border-[#E8E6E1] shadow-[-10px_0_30px_rgba(0,0,0,0.05)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#E8E6E1] p-6">
              <div className="flex items-center gap-2 text-[#B89154]">
                <Sparkles className="h-5 w-5" strokeWidth={1.5} />
                <h3 className="font-serif text-xl" style={{ fontFamily: '"Playfair Display", serif' }}>
                  {t("common.ai_assist")}
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[#8A8886] hover:text-[#1A1A1A] transition-colors"
                data-testid={`ai-assist-close-${module}-btn`}
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {suggestions.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#8A8886]">
                    {lang === "es" ? "Sugerencias" : "Suggestions"}
                  </div>
                  <div className="flex flex-col gap-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => ask(s)}
                        className="text-left text-sm border border-[#E8E6E1] hover:border-[#B89154] hover:bg-[#F5EFE6]/50 rounded-sm px-3 py-2 transition-all text-[#1A1A1A]"
                        data-testid={`ai-suggestion-${module}-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loading && (
                <div className="text-[#8A8886] text-sm italic">{t("common.generating")}</div>
              )}

              {response && (
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#8A8886]">
                    {lang === "es" ? "Respuesta" : "Response"}
                  </div>
                  <div
                    className="bg-[#F5EFE6]/40 border border-[#E8E6E1] rounded-sm p-4 text-sm text-[#1A1A1A] whitespace-pre-wrap leading-relaxed"
                    data-testid={`ai-response-${module}`}
                  >
                    {response}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={copy}
                      className="inline-flex items-center gap-2 text-xs border border-[#E8E6E1] hover:border-[#1A1A1A] rounded-sm px-3 py-1.5 text-[#1A1A1A] transition-all"
                      data-testid={`ai-copy-${module}-btn`}
                    >
                      <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                      {t("common.copy")}
                    </button>
                    {onApply && (
                      <button
                        onClick={() => {
                          onApply(response);
                          toast.success(lang === "es" ? "Aplicado" : "Applied");
                        }}
                        className="inline-flex items-center gap-2 text-xs bg-[#2563EB] text-[#F9F8F6] hover:bg-[#1D4ED8] rounded-sm px-3 py-1.5 transition-all"
                        data-testid={`ai-apply-${module}-btn`}
                      >
                        {t("common.apply")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-[#E8E6E1] p-4 space-y-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t("common.ai_placeholder")}
                rows={3}
                className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-sm px-3 py-2 outline-none resize-none"
                data-testid={`ai-prompt-${module}-textarea`}
              />
              <button
                onClick={() => ask()}
                disabled={loading || !prompt.trim()}
                className="inline-flex items-center justify-center gap-2 w-full bg-[#2563EB] text-[#F9F8F6] hover:bg-[#1D4ED8] rounded-sm px-4 py-2.5 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`ai-send-${module}-btn`}
              >
                <Send className="h-4 w-4" strokeWidth={1.5} />
                {t("common.ask_ai")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
